import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import {
  initLiff,
  getLineProfile,
  loginWithLine,
  broadcastBindingApproved
} from '../../lib/lineAuth';

const MobileBindingConfirmCard = ({ token, userId, onReturnHome }) => {
  const [loading, setLoading] = useState(true);
  const [lineProfile, setLineProfile] = useState(null);
  const [staffUser, setStaffUser] = useState(null);
  const [approving, setApproving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isDevStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'dev.fangdent.com' || process.env.REACT_APP_ENV === 'staging');

  // Check initial token status
  const checkTokenStatus = useCallback(async () => {
    if (!token || !supabase) return false;
    try {
      const { data, error } = await supabase.rpc('check_binding_status', { p_token: token });
      if (!error && Array.isArray(data) && data.length > 0) {
        if (data[0].status === 'approved') {
          setIsSuccess(true);
          return true;
        }
      }
    } catch (err) {
      console.warn('checkTokenStatus error:', err);
    }
    return false;
  }, [token]);

  // Load LINE profile and staff user
  const loadProfileAndStaff = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const alreadyApproved = await checkTokenStatus();
      if (alreadyApproved) {
        setLoading(false);
        return;
      }

      // Fetch target staff user
      if (userId && supabase) {
        const { data: staffData } = await supabase
          .from('user_profiles')
          .select('id, email, name, role, first_name, last_name')
          .eq('id', userId)
          .maybeSingle();

        if (staffData) {
          setStaffUser(staffData);
        }
      }

      await initLiff();
      const profile = await getLineProfile();
      if (profile && profile.userId) {
        setLineProfile(profile);
      }
    } catch (err) {
      console.error('MobileBindingConfirmCard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [checkTokenStatus, userId]);

  useEffect(() => {
    loadProfileAndStaff();
  }, [loadProfileAndStaff]);

  const handleApprove = async () => {
    if (!token) return;
    if (!lineProfile || !lineProfile.userId) {
      loginWithLine({ redirectUri: window.location.href });
      return;
    }

    setApproving(true);
    setErrorMessage('');
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase.rpc('approve_line_binding', {
        p_token: token,
        p_line_user_id: lineProfile.userId,
        p_line_display_name: lineProfile.displayName || 'LINE User',
        p_line_picture_url: lineProfile.pictureUrl || ''
      });

      if (error) {
        setErrorMessage(error.message || 'เกิดข้อผิดพลาดในการยืนยันการเชื่อมต่อ LINE');
      } else if (data === false) {
        setErrorMessage('ไม่สามารถยืนยันได้ โทเค็นอาจหมดอายุหรือถูกใช้งานแล้ว');
      } else {
        // Broadcast in realtime to desktop
        await broadcastBindingApproved(token, {
          userId: lineProfile.userId,
          displayName: lineProfile.displayName,
          pictureUrl: lineProfile.pictureUrl
        });

        setIsSuccess(true);
      }
    } catch (err) {
      console.error('Approval exception:', err);
      setErrorMessage(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setApproving(false);
    }
  };

  const handleClose = () => {
    try {
      window.close();
    } catch (e) {}
    if (onReturnHome) {
      onReturnHome();
    } else if (typeof window !== 'undefined') {
      window.location.href = window.location.origin;
    }
  };

  if (isSuccess) {
    return (
      <div
        data-testid="mobile-binding-success-view"
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #06C755 0%, #059669 100%)',
          padding: '20px',
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            maxWidth: '420px',
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            padding: '36px 24px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}
        >
          {isDevStaging && (
            <div
              style={{
                marginBottom: '16px',
                padding: '6px 12px',
                backgroundColor: '#fff7ed',
                border: '1.5px solid #f97316',
                borderRadius: '12px',
                color: '#c2410c',
                fontSize: '0.82em',
                fontWeight: '700'
              }}
            >
              🧪 Dev Staging Environment
            </div>
          )}

          <div style={{ fontSize: '54px', marginBottom: '14px' }}>🎉</div>
          <h2 style={{ fontSize: '1.35em', fontWeight: 'bold', color: '#15803d', margin: '0 0 10px 0' }}>
            🎉 ยืนยันเชื่อมต่อ LINE สำเร็จแล้ว!
          </h2>
          <p style={{ color: '#166534', fontSize: '1em', lineHeight: '1.6', margin: '0 0 20px 0' }}>
            ข้อมูล LINE ของท่านได้รับการเชื่อมต่อกับระบบทันตกรรมเรียบร้อยแล้ว ท่านสามารถปิดแท็บนี้และสลับกลับไปที่หน้าเปิดใช้งานเพื่อกดลงทะเบียนได้ทันที
          </p>

          <button
            type="button"
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#06C755',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(6, 199, 85, 0.4)'
            }}
          >
            ✅ ปิดหน้านี้และกลับไปที่หน้าลงทะเบียน
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          maxWidth: '440px',
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          padding: '32px 24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
          textAlign: 'center'
        }}
      >
        {isDevStaging && (
          <div
            style={{
              marginBottom: '16px',
              padding: '6px 12px',
              backgroundColor: '#fff7ed',
              border: '1.5px solid #f97316',
              borderRadius: '12px',
              color: '#c2410c',
              fontSize: '0.82em',
              fontWeight: '700'
            }}
          >
            🧪 Dev Staging Environment
          </div>
        )}

        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            backgroundColor: '#e0f2fe',
            color: '#0284c7',
            fontSize: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px'
          }}
        >
          🏥
        </div>

        <h2 style={{ fontSize: '1.3em', fontWeight: 'bold', color: '#1e293b', margin: '0 0 6px 0' }}>
          เชื่อมต่อบัญชี LINE
        </h2>
        <p style={{ fontSize: '0.9em', color: '#64748b', margin: '0 0 20px 0' }}>
          ระบบ CSSD โรงพยาบาลทันตกรรม
        </p>

        {/* Staff details */}
        {staffUser && (
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '14px',
              padding: '14px',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '2px' }}>
              เชื่อมต่อกับบัญชีเจ้าหน้าที่:
            </div>
            <div style={{ fontSize: '1.05em', fontWeight: 'bold', color: '#1e293b' }}>
              {staffUser.name ||
                `${staffUser.first_name || ''} ${staffUser.last_name || ''}`.trim() ||
                staffUser.email}
            </div>
            {staffUser.role && (
              <div style={{ fontSize: '0.8em', color: '#7c3aed', marginTop: '2px' }}>
                บทบาท: {staffUser.role}
              </div>
            )}
          </div>
        )}

        {/* LINE Profile Card or Login Prompt */}
        {loading ? (
          <div style={{ padding: '24px 0', color: '#64748b', fontSize: '0.95em' }}>
            ⏳ กำลังโหลดข้อมูล LINE...
          </div>
        ) : lineProfile ? (
          <div
            style={{
              backgroundColor: '#f0fdf4',
              border: '1.5px solid #86efac',
              borderRadius: '16px',
              padding: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              marginBottom: '20px',
              textAlign: 'left'
            }}
          >
            {lineProfile.pictureUrl ? (
              <img
                src={lineProfile.pictureUrl}
                alt="LINE Avatar"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #06C755'
                }}
              />
            ) : (
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#06C755',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}
              >
                💬
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.78em', color: '#166534', fontWeight: '700' }}>
                บัญชี LINE ของท่าน:
              </div>
              <div
                style={{
                  fontSize: '1em',
                  fontWeight: 'bold',
                  color: '#1e293b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {lineProfile.displayName}
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#fffbeb',
              border: '1.5px solid #fde68a',
              borderRadius: '14px',
              padding: '14px',
              color: '#b45309',
              fontSize: '0.88em',
              marginBottom: '20px',
              textAlign: 'left'
            }}
          >
            💬 กรุณาเข้าสู่ระบบ LINE เพื่อให้ระบบสามารถดึงข้อมูลบัญชีของคุณได้
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '10px',
              color: '#b91c1c',
              fontSize: '0.85em',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            ⚠️ {errorMessage}
          </div>
        )}

        {/* Action Button */}
        {lineProfile ? (
          <button
            type="button"
            disabled={approving}
            onClick={handleApprove}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#06C755',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: approving ? 'wait' : 'pointer',
              boxShadow: '0 4px 14px rgba(6, 199, 85, 0.35)',
              marginBottom: '10px'
            }}
          >
            {approving ? '⏳ กำลังยืนยัน...' : 'ยืนยันการเชื่อมต่อ LINE'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => loginWithLine({ redirectUri: window.location.href })}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#06C755',
              color: '#ffffff',
              border: 'none',
              borderRadius: '14px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(6, 199, 85, 0.35)',
              marginBottom: '10px'
            }}
          >
            เข้าสู่ระบบด้วย LINE
          </button>
        )}

        <button
          type="button"
          onClick={handleClose}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#f1f5f9',
            color: '#64748b',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.92em',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ยกเลิก / ปิดหน้าต่าง
        </button>
      </div>
    </div>
  );
};

export default MobileBindingConfirmCard;
